/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

// Light search-state seam. The header (app-shell chunk) needs the live
// query controller and route-mode observable, but must NOT drag the full
// `Search.tsx` page (shovelers, i18n bundle, searchService) into the
// shell bundle — the page itself is lazy-loaded by the `/search` route.

import { data, type MutableValue, type ObservableValue } from '@amazon/vinyl-observable'
import { globalRef, type Unsubscribe } from '@amazon/vinyl-util'
import { back, navigateTo, preloadRoute, setCurrentUrl } from '@/router/router'
import type { SearchOptions, SpellCorrectionFeature } from '@/search/SearchRecommender'

const searchControllerRef = globalRef<MutableValue<SearchOptions | null>>(() =>
    data<SearchOptions | null>(null)
)

/**
 * A singleton to the search controller.
 *
 * The controller stores the live query string. Visibility of the search
 * page is now driven by the URL (`/search`), not by setting this to
 * null — see `getSearchMode()`.
 */
export function getSearchController() {
    return searchControllerRef.value
}

const searchRouteRef = globalRef<MutableValue<boolean>>(() => data(false))

/**
 * Tracks whether we're on the `/search` route. Wired to the router by
 * `Search` itself on mount; the page won't be reachable without that
 * mount happening, so this stays in sync.
 */
export function getSearchRouteRef(): MutableValue<boolean> {
    return searchRouteRef.value
}

const searchSubmittedRef = globalRef<MutableValue<boolean>>(() => data(false))

/**
 * True once the query was committed by an explicit submit gesture (Enter,
 * search icon, tapping a history/suggestion pill, or a deep-linked results
 * URL); false while the user is typing live. GH surfaces related searches
 * only in its typing/suggestions state, so the results view keys its
 * related-searches block off this being false to match.
 */
export function getSearchSubmitted(): MutableValue<boolean> {
    return searchSubmittedRef.value
}

export function getSearchMode(): ObservableValue<boolean> {
    return getSearchRouteRef()
}

// Focus seam for the header search input so the global `/` shortcut can focus
// it without threading a ref through the shell. Mirrors the chat input's
// `chatInputFocus` seam; the header search is a singleton, so one slot suffices.
let searchInputEl: HTMLInputElement | null = null

export function registerSearchInput(el: HTMLInputElement): Unsubscribe {
    searchInputEl = el
    return () => {
        if (searchInputEl === el) searchInputEl = null
    }
}

export function focusSearchInput(): void {
    searchInputEl?.focus()
}

export function clearSearch() {
    // Drop the live query state and leave the search route if we're on
    // it. `back()` returns to wherever the user came from (e.g. home);
    // there's no harm in calling it from elsewhere — same-path
    // navigations are no-ops at the router layer.
    getSearchController().value = null
    getSearchSubmitted().value = false
    if (window.location.pathname.startsWith('/search')) back()
}

// The route never varies, so one warm per session covers every later
// hover/focus — repeat warms would just rebuild the same page for no benefit.
const searchRoutePreloadedRef = globalRef(() => ({ done: false }))

/**
 * Warms the `/search` chunk from a header search entry point (hover / focus),
 * once per session.
 */
export function preloadSearchRoute(): void {
    const preloaded = searchRoutePreloadedRef.value
    if (preloaded.done) return
    preloaded.done = true
    preloadRoute('/search')
}

export function showSearch(query?: string) {
    // Initialize the controller's query so the input pre-populates if a
    // caller seeded one (deep link with ?q=). The route mount kicks the
    // search-mode observable on, which the header listens to.
    getSearchController().value = { query: query ?? '' }
    const q = query?.trim()
    // Navigating in with a query is a submit gesture; an empty entry just
    // opens the idle pane.
    getSearchSubmitted().value = !!q
    navigateTo(q ? `/search?q=${encodeURIComponent(q)}` : '/search')
}

export interface UpdateSearchQueryOptions {
    readonly spellCorrection?: SpellCorrectionFeature
    /**
     * Fork a new history entry for this update (Back returns to the prior
     * query) instead of rewriting the current one. The page passes `true`
     * when leaving a stable back-target — the idle landing or a sealed
     * submitted query — and `false` for as-you-type edits of a live draft.
     */
    readonly pushHistory?: boolean
}

/**
 * Sync the `?q=` search param and controller with a query update. The URL
 * stays canonical (link-shareable, popstate-restorable); `pushHistory`
 * decides whether Back walks to the prior query (a submitted query / a jump
 * to a new query) or the current entry is rewritten in place (as-you-type).
 * Routed through the router's `setCurrentUrl` so the in-app history depth and
 * `currentPath` stay in sync with the entries we add. Callers on the search
 * route only.
 */
export function updateSearchQuery(query: string, opts?: UpdateSearchQueryOptions): void {
    const controller = getSearchController()
    const trimmed = query.trim()
    if (opts?.spellCorrection) {
        // A spell-correction action must re-fire even when the query text is
        // unchanged — rejecting an autocorrect reuses the original typed term.
        controller.value = { query, spellCorrection: opts.spellCorrection }
    } else if (controller.value?.query !== query) {
        controller.value = { query }
    }
    const url = new URL(window.location.href)
    if (trimmed) {
        url.searchParams.set('q', trimmed)
    } else {
        url.searchParams.delete('q')
    }
    if (url.href !== window.location.href) {
        setCurrentUrl(url.pathname + url.search, { push: opts?.pushHistory ?? false })
    }
}

/**
 * Mirror the selected result-type filter into the `?type=` search param. A
 * user pill toggle (`pushHistory: true`) forks a back-stop so Back returns to
 * the prior filter; the automatic drop of a stale filter on a query change
 * rewrites the current entry. `null` clears the filter. Callers on the search
 * route only.
 */
export function updateSearchType(
    type: string | null,
    opts?: { readonly pushHistory?: boolean }
): void {
    const url = new URL(window.location.href)
    if (type) {
        url.searchParams.set('type', type)
    } else {
        url.searchParams.delete('type')
    }
    if (url.href !== window.location.href) {
        setCurrentUrl(url.pathname + url.search, { push: opts?.pushHistory ?? false })
    }
}
